/**
 * VisitsBlock — the Overview's Visits block: the patient's previous and next visit as cards (patient pages
 * switch; prototype commit b8afdd2, design log 13 Oct "Visits, both as calendar cards led by their date").
 * Taller, coloured by the visit type like the calendar's card (the appointment profile's colour, drawn in
 * the muted category ramp by `categoryHue`), the date on top, and the status and "Open encounter" INSIDE
 * the card. A past visit is the muted grey card. Same `usePatientAppointmentsForChart` read as the chart's
 * Appointments section; Open encounter opens the existing `/encounter/:appointmentId` route.
 */
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Skeleton, StatusChip, appointmentStatusSemantic } from '@scripsteam/scrips-signal-ds';
import { usePatientAppointmentsForChart } from '@/patients/services/use-patient-appointments-for-chart';
import { nextAndPreviousVisit, visitWhen } from '@/patients/lib/overview-headlines';
import { appointmentStartBlockReason } from '@/encounter/lib/is-arrived';
import { categoryHue } from '@/shared/lib/category-color';
import { OverviewBlock } from './overview-block';
import type { AppointmentForPatient } from '@/types/scheduling-api';

export function VisitsBlock({ patientId, onOpen }: { patientId: string; onOpen: () => void }) {
  const { data, isLoading, isError } = usePatientAppointmentsForChart(patientId);
  const { next, previous } = nextAndPreviousVisit(data?.appointments);
  return (
    <OverviewBlock title="Visits" icon={<Icon name="calendar" size={16} color="currentColor" />} linkLabel="Appointments" onOpen={onOpen} wide>
      {isLoading ? (
        <Skeleton className="h-10 w-full" />
      ) : isError ? (
        <p data-slot="overview-empty">Could not load. Try again later.</p>
      ) : (
        <div data-slot="overview-visits">
          <VisitCard label="Previous visit" appt={previous} past empty="No earlier visit" />
          <VisitCard label="Next visit" appt={next} empty="No upcoming visit" />
        </div>
      )}
    </OverviewBlock>
  );
}

function VisitCard({
  label,
  appt,
  past = false,
  empty,
}: {
  label: string;
  appt: AppointmentForPatient | null;
  past?: boolean;
  empty: string;
}) {
  const navigate = useNavigate();
  if (!appt) {
    return (
      <div data-slot="visit-card" data-past="true" data-empty="true">
        <span data-slot="visit-card-label">{label}</span>
        <span data-slot="visit-card-what">{empty}</span>
      </div>
    );
  }
  const code = appt.status?.code;
  // The same gate the encounter's own Start uses: enabled once the patient has arrived (or the visit is done).
  const startReason = code ? appointmentStartBlockReason(code) : 'Please update the appointment status to start the encounter.';
  const hue = past ? null : categoryHue(appt.appointmentProfile?.color);
  return (
    <div data-slot="visit-card" data-past={past ? 'true' : undefined} data-hue={hue ?? undefined}>
      <span data-slot="visit-card-label">{label}</span>
      <span data-slot="visit-card-when">{visitWhen(appt.start)}</span>
      <span data-slot="visit-card-what">
        {appt.reasonCode?.display?.trim() || appt.specialty?.code?.displayName?.trim() || 'Visit'}
      </span>
      <div data-slot="visit-card-foot">
        {/* The visit's status as the soft status chip (design PaVisitCard), coloured by the appointment status. */}
        {appt.status?.display ? (
          <StatusChip semantic={appointmentStatusSemantic(code)} tone="soft">
            {appt.status.display}
          </StatusChip>
        ) : null}
        {past ? null : (
          <Button
            type="button"
            size="sm"
            disabled={startReason !== undefined}
            title={startReason}
            onClick={() => void navigate(`/encounter/${appt.id}`)}
          >
            Open encounter
          </Button>
        )}
      </div>
    </div>
  );
}
